import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { Event } from '../types/event.types';
import { locationCache } from '../services/LocationCacheService';

// ===== VIEW MODE =====
export type ViewMode = 'list' | 'map';

// ===== PENDING REMOVAL (pour animations favoris) =====
export interface PendingRemoval {
  id: string;
  eventTitle: string;
  eventData: Event;
  originalIndex: number;
  startTime: number;
  status: 'exiting' | 'pending_undo' | 'restoring';
}

const UNDO_DELAY_MS = 6000;
const EXIT_ANIMATION_DURATION_MS = 400;

// Timeouts gérés en dehors du store (non-sérialisables)
const pendingRemovalTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

interface AppState {
  // ===== VIEW MODE =====
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Gestion des événements masqués
  hiddenEvents: Record<string, { reason: 'favorite' | 'dismiss'; timestamp: number }>;

  // ===== PENDING REMOVALS (remplace les variables globales de FavoritesAnimatedList) =====
  pendingRemovals: Record<string, PendingRemoval>;
  
  // ✅ NOUVEAU : State management complet des favoris
  favoriteEvents: Event[]; // Liste complète des événements favoris
  favoriteIds: Set<string>; // Set optimisé pour les vérifications rapides
  
  // Actions pour les événements masqués
  addToHiddenEvents: (eventId: string, reason: 'favorite' | 'dismiss') => void;
  removeFromHiddenEvents: (eventId: string) => Promise<void>;
  clearHiddenEvents: () => void;
  isEventHidden: (eventId: string) => boolean;
  // ✅ RESTAURÉ : Méthodes pour compatibilité avec composants existants
  isHidden: (eventId: string, seriesId?: string) => boolean; // Alias pour compatibilité
  undoLastRemoval: () => Promise<void>;
  getExitReason: (eventId: string) => 'favorite' | 'dismiss' | undefined;
  
  // Actions pour les favoris
  toggleFavorite: (id: string, expiresAt?: Date, onFavoriteChange?: (isFavorite: boolean) => void, fullEvent?: Event, seriesEvents?: Event[]) => Promise<void>;
  isFavorite: (eventId: string) => boolean;
  loadFavorites: () => Promise<void>;
  syncWithCache: () => Promise<void>;

  // ===== PENDING REMOVALS ACTIONS =====
  addPendingRemoval: (id: string, eventTitle: string, eventData: Event, originalIndex: number) => void;
  undoPendingRemoval: (id: string) => Promise<void>;
  clearPendingRemoval: (id: string) => void;
  transitionToUndoPending: (id: string) => void;
  getPendingRemoval: (id: string) => PendingRemoval | undefined;
  updatePendingRemovalIndex: (id: string, newIndex: number) => void;
}

// ===== DEBOUNCE & LOADING FLAGS =====
let isLoadingFavorites = false;
let lastLoadFavoritesTime = 0;
const LOAD_FAVORITES_DEBOUNCE_MS = 300; // Minimum time between calls

// ===== STORE IMPLEMENTATION =====
export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // ===== INITIAL STATE =====
      viewMode: (typeof window !== 'undefined' ? localStorage.getItem('lastViewMode') as ViewMode : null) || 'list',
      hiddenEvents: {},
      favoriteEvents: [],
      favoriteIds: new Set(),
      pendingRemovals: {},

      // ===== VIEW MODE METHODS =====
      setViewMode: (mode: ViewMode) => {
        set({ viewMode: mode });
        if (typeof window !== 'undefined') {
          localStorage.setItem('lastViewMode', mode);
          // Dispatch event pour compatibilité avec composants legacy
          window.dispatchEvent(new CustomEvent('viewModeChange', { detail: mode }));
        }
      },

      // ===== HIDDEN EVENTS METHODS =====
      addToHiddenEvents: async (eventId: string, reason: 'favorite' | 'dismiss') => {
        const timestamp = Date.now();
        try {
          const indexedDBReason = reason === 'dismiss' ? 'swipe' : reason;
          await locationCache.saveHiddenEvent(eventId, indexedDBReason);
          set(state => ({
            hiddenEvents: { ...state.hiddenEvents, [eventId]: { reason, timestamp } }
          }));
          window.dispatchEvent(new CustomEvent('hiddenEventsUpdated', {
            detail: { action: 'add', eventId, reason }
          }));
        } catch (error) {
          console.error('AppStore - addToHiddenEvents error:', error);
        }
      },

      removeFromHiddenEvents: async (eventId: string) => {
        try {
          await locationCache.removeHiddenEvent(eventId);
          set(state => {
            const newHiddenEvents = { ...state.hiddenEvents };
            delete newHiddenEvents[eventId];
            return { hiddenEvents: newHiddenEvents };
          });
          window.dispatchEvent(new CustomEvent('hiddenEventsUpdated', {
            detail: { action: 'remove', eventId }
          }));
        } catch (error) {
          console.error('AppStore - removeFromHiddenEvents error:', error);
        }
      },

      clearHiddenEvents: () => {
        set({ hiddenEvents: {} });
        window.dispatchEvent(new CustomEvent('hiddenEventsUpdated', { detail: { action: 'clear' } }));
      },

      isEventHidden: (eventId: string): boolean => {
        const hiddenEvent = get().hiddenEvents[eventId];
        return !!hiddenEvent;
      },

      isHidden: (eventId: string, seriesId?: string): boolean => {
        const targetId = seriesId || eventId;
        return !!get().hiddenEvents[targetId];
      },

      undoLastRemoval: async (): Promise<void> => {
        try {
          const { hiddenEvents } = get();
          const dismissedEvents = Object.entries(hiddenEvents)
            .filter(([_, event]) => event.reason === 'dismiss')
            .sort(([_, a], [__, b]) => b.timestamp - a.timestamp);

          if (dismissedEvents.length === 0) return;

          const [eventIdToRestore] = dismissedEvents[0];
          await get().removeFromHiddenEvents(eventIdToRestore);
        } catch (error) {
          console.error('AppStore - undoLastRemoval error:', error);
        }
      },

      getExitReason: (eventId: string): 'favorite' | 'dismiss' | undefined => {
        const hiddenEvent = get().hiddenEvents[eventId];
        return hiddenEvent?.reason;
      },

      // ===== FAVORITES METHODS =====
      toggleFavorite: async (id: string, _expiresAt?: Date, onFavoriteChange?: (isFavorite: boolean) => void, fullEvent?: Event, seriesEvents?: Event[]) => {
        try {
          const { favoriteIds } = get();
          const storageId = id;
          const eventToStore = fullEvent;
          const isCurrentlyFavorite = favoriteIds.has(storageId);
          const shouldFavorite = !isCurrentlyFavorite;

          if (shouldFavorite) {
            if (eventToStore) {
              // 🆕 Si l'événement a un seriesId, charger TOUTES les occurrences depuis IndexedDB geoEvents
              if (eventToStore.seriesId) {
                console.log('🔍 [AppStore] Chargement de toutes les occurrences de la série depuis geoEvents:', eventToStore.seriesId);

                // Récupérer tous les événements de la série depuis geoEvents IndexedDB
                const allSeriesEvents = await locationCache.getGeoEventsBySeriesId(eventToStore.seriesId);

                if (allSeriesEvents && allSeriesEvents.length > 0) {
                  console.log('💾 [AppStore] Sauvegarde série complète:', allSeriesEvents.length, 'événements depuis geoEvents');
                  // Copier toutes les occurrences dans favorite_events
                  await locationCache.saveSeriesToFavorites(allSeriesEvents);
                  await get().addToHiddenEvents(storageId, 'favorite');
                  set(state => {
                    const newFavoriteEvents = [...state.favoriteEvents, ...allSeriesEvents];
                    const newFavoriteIds = new Set(state.favoriteIds);
                    newFavoriteIds.add(storageId);
                    return { favoriteEvents: newFavoriteEvents, favoriteIds: newFavoriteIds };
                  });
                } else {
                  console.warn('⚠️ [AppStore] Aucune occurrence trouvée dans geoEvents pour la série, sauvegarde événement unique');
                  await locationCache.saveFavoriteEvent(eventToStore);
                  await get().addToHiddenEvents(storageId, 'favorite');
                  set(state => {
                    const newFavoriteEvents = [...state.favoriteEvents, eventToStore];
                    const newFavoriteIds = new Set(state.favoriteIds);
                    newFavoriteIds.add(storageId);
                    return { favoriteEvents: newFavoriteEvents, favoriteIds: newFavoriteIds };
                  });
                }
              } else {
                // Événement simple : comportement actuel
                await locationCache.saveFavoriteEvent(eventToStore);
                await get().addToHiddenEvents(storageId, 'favorite');
                set(state => {
                  const newFavoriteEvents = [...state.favoriteEvents, eventToStore];
                  const newFavoriteIds = new Set(state.favoriteIds);
                  newFavoriteIds.add(storageId);
                  return { favoriteEvents: newFavoriteEvents, favoriteIds: newFavoriteIds };
                });
              }
              onFavoriteChange?.(true);
            }
          } else {
            // Retrait des favoris
            if (eventToStore?.seriesId) {
              // 🆕 Si série : supprimer TOUS les événements
              console.log('🗑️ [AppStore] Suppression série complète:', eventToStore.seriesId);
              await locationCache.removeSeriesFromFavorites(eventToStore.seriesId);
            } else if (eventToStore) {
              // Événement simple
              await locationCache.removeFavoriteEvent(eventToStore.id);
            }
            await get().removeFromHiddenEvents(storageId);
            set(state => {
              const newFavoriteEvents = state.favoriteEvents.filter(event => {
                return event.id !== (eventToStore?.id || storageId) &&
                       (event.seriesId || event.id) !== storageId;
              });
              const newFavoriteIds = new Set(state.favoriteIds);
              newFavoriteIds.delete(storageId);
              return { favoriteEvents: newFavoriteEvents, favoriteIds: newFavoriteIds };
            });
            onFavoriteChange?.(false);
          }

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('favoritesChanged', {
              detail: { eventId: id, isFavorite: shouldFavorite }
            }));
          }, 100);
        } catch (error) {
          console.error('AppStore - toggleFavorite error:', error);
        }
      },

      isFavorite: (eventId: string): boolean => {
        const { favoriteIds } = get();
        return favoriteIds.has(eventId);
      },

             loadFavorites: async () => {
        try {
          const now = Date.now();
          if (isLoadingFavorites) return;
          if (now - lastLoadFavoritesTime < LOAD_FAVORITES_DEBOUNCE_MS) return;

          isLoadingFavorites = true;
          lastLoadFavoritesTime = now;

          const cachedFavoriteEvents = await locationCache.getFavoriteEventsFromDB();

          if (cachedFavoriteEvents.length === 0) {
            set({ favoriteEvents: [], favoriteIds: new Set() });
            isLoadingFavorites = false;
            return;
          }

          const validEvents = cachedFavoriteEvents.filter((event: any) => {
            try {
              const currentTime = new Date();
              let eventExpiresAt: Date;

              if (event.endTime) {
                const endTime = new Date(event.endTime);
                eventExpiresAt = isNaN(endTime.getTime())
                  ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                  : endTime;
              } else if (event.datetime) {
                const datetime = new Date(event.datetime);
                eventExpiresAt = isNaN(datetime.getTime())
                  ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                  : new Date(datetime.getTime() + 24 * 60 * 60 * 1000);
              } else {
                eventExpiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              }

              return eventExpiresAt > currentTime;
            } catch {
              return true;
            }
          });

          const { hiddenEvents } = get();
          const favoriteIds = new Set(validEvents.map((event: any) => {
            const storageId = event.seriesId || event.id;
            const isInHiddenAsFavorite = hiddenEvents[storageId]?.reason === 'favorite';
            return isInHiddenAsFavorite ? storageId : null;
          }).filter(Boolean));

          const finalValidEvents = validEvents.filter((event: any) => {
            const storageId = event.seriesId || event.id;
            return favoriteIds.has(storageId);
          });

          // Cleanup orphan events in IndexedDB
          if (finalValidEvents.length < validEvents.length) {
            const orphanEvents = validEvents.filter((event: any) => {
              const storageId = event.seriesId || event.id;
              return !favoriteIds.has(storageId);
            });
            Promise.all(orphanEvents.map((event: any) =>
              locationCache.removeFavoriteEvent(event.id).catch(() => {})
            ));
          }

          // 🔧 FIX: Cleanup orphan hiddenEvents (marked as 'favorite' but not in favoriteIds)
          const cleanedHiddenEvents = { ...hiddenEvents };
          let hasOrphanHiddenEvents = false;
          Object.entries(hiddenEvents).forEach(([eventId, data]) => {
            if (data.reason === 'favorite' && !favoriteIds.has(eventId)) {
              console.log(`🧹 [AppStore] Nettoyage hiddenEvent orphelin: ${eventId}`);
              delete cleanedHiddenEvents[eventId];
              locationCache.removeHiddenEvent(eventId).catch(() => {});
              hasOrphanHiddenEvents = true;
            }
          });

          if (hasOrphanHiddenEvents) {
            set({ favoriteEvents: finalValidEvents, favoriteIds, hiddenEvents: cleanedHiddenEvents });
          } else {
            set({ favoriteEvents: finalValidEvents, favoriteIds });
          }

          window.dispatchEvent(new CustomEvent('favoritesLoaded', {
            detail: { count: finalValidEvents.length, favoriteIds: Array.from(favoriteIds) }
          }));

          isLoadingFavorites = false;
        } catch (error) {
          console.error('AppStore - loadFavorites error:', error);
          set({ favoriteEvents: [], favoriteIds: new Set() });
          isLoadingFavorites = false;
        }
      },

      syncWithCache: async () => {
        try {
          await get().loadFavorites();

          const cachedHiddenEvents = await locationCache.getAllHiddenEvents();
          const hiddenEventsMap: Record<string, { reason: 'favorite' | 'dismiss'; timestamp: number }> = {};

          cachedHiddenEvents.forEach((hiddenEvent: any) => {
            const zustandReason = hiddenEvent.reason === 'swipe' ? 'dismiss' : hiddenEvent.reason;
            hiddenEventsMap[hiddenEvent.eventId] = {
              reason: zustandReason as 'favorite' | 'dismiss',
              timestamp: hiddenEvent.timestamp
            };
          });

          set(state => ({ ...state, hiddenEvents: hiddenEventsMap }));
        } catch (error) {
          console.error('AppStore - syncWithCache error:', error);
        }
      },

      // ===== PENDING REMOVALS METHODS =====
      addPendingRemoval: (id: string, eventTitle: string, eventData: Event, originalIndex: number) => {
        // Ignorer si déjà en cours de traitement
        if (get().pendingRemovals[id]) return;

        const startTime = Date.now();
        console.log(`🟡 addPendingRemoval: ${id.slice(0,8)}... status=exiting`);

        // Ajouter avec status 'exiting'
        set(state => ({
          pendingRemovals: {
            ...state.pendingRemovals,
            [id]: { id, eventTitle, eventData, originalIndex, startTime, status: 'exiting' }
          }
        }));

        // Transition vers 'pending_undo' après l'animation de sortie
        setTimeout(() => {
          console.log(`🟠 transitionToUndoPending timeout fired for: ${id.slice(0,8)}...`);
          get().transitionToUndoPending(id);
        }, EXIT_ANIMATION_DURATION_MS);

        // Timeout pour suppression automatique
        const timeoutId = setTimeout(() => {
          get().clearPendingRemoval(id);
        }, UNDO_DELAY_MS);

        pendingRemovalTimeouts.set(id, timeoutId);
      },

      transitionToUndoPending: (id: string) => {
        set(state => {
          const removal = state.pendingRemovals[id];
          if (!removal || removal.status !== 'exiting') {
            console.log(`⚠️ transitionToUndoPending SKIPPED: ${id.slice(0,8)}... (removal=${!!removal}, status=${removal?.status})`);
            return state;
          }
          console.log(`🟢 transitionToUndoPending: ${id.slice(0,8)}... exiting → pending_undo`);
          return {
            pendingRemovals: {
              ...state.pendingRemovals,
              [id]: { ...removal, status: 'pending_undo' }
            }
          };
        });
      },

      undoPendingRemoval: async (id: string) => {
        const removal = get().pendingRemovals[id];
        if (!removal) return;

        // Annuler le timeout de suppression
        const timeoutId = pendingRemovalTimeouts.get(id);
        if (timeoutId) {
          clearTimeout(timeoutId);
          pendingRemovalTimeouts.delete(id);
        }

        // Marquer comme 'restoring'
        set(state => ({
          pendingRemovals: {
            ...state.pendingRemovals,
            [id]: { ...removal, status: 'restoring' }
          }
        }));

        // Remettre en favori
        if (removal.eventData) {
          await get().toggleFavorite(id, undefined, undefined, removal.eventData);
        }

        // Nettoyer après l'animation de retour (400ms = durée de l'animation)
        setTimeout(() => {
          get().clearPendingRemoval(id);
        }, 450);
      },

      clearPendingRemoval: (id: string) => {
        console.log(`🔴 clearPendingRemoval: ${id.slice(0,8)}...`);
        // Nettoyer le timeout si existe
        const timeoutId = pendingRemovalTimeouts.get(id);
        if (timeoutId) {
          clearTimeout(timeoutId);
          pendingRemovalTimeouts.delete(id);
        }

        set(state => {
          const { [id]: _, ...rest } = state.pendingRemovals;
          return { pendingRemovals: rest };
        });
      },

      getPendingRemoval: (id: string) => {
        return get().pendingRemovals[id];
      },

      updatePendingRemovalIndex: (id: string, newIndex: number) => {
        set(state => {
          const removal = state.pendingRemovals[id];
          if (!removal) return state;
          return {
            pendingRemovals: {
              ...state.pendingRemovals,
              [id]: { ...removal, originalIndex: newIndex }
            }
          };
        });
      }
    }),
    {
      name: 'app-store',
      // ✅ CORRECTION : Persister aussi les favoriteIds pour éviter la déconnexion
      partialize: (state) => ({
        hiddenEvents: state.hiddenEvents,
        favoriteIds: Array.from(state.favoriteIds) // Convertir Set en Array pour la persistance
      }),
      // Hydrater correctement le Set depuis l'Array
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray((state as any).favoriteIds)) {
          state.favoriteIds = new Set((state as any).favoriteIds);
        }
      },
      version: 2, // ⬆️ Incrémenter pour forcer la migration
    }
  )
);

// ===== HOOKS UTILITAIRES (useShallow pour comparaison superficielle et éviter re-renders inutiles) =====
export const useHiddenEvents = () => useAppStore(
  useShallow((state) => ({
    hiddenEvents: state.hiddenEvents,
    hiddenEventsDetails: state.hiddenEvents,
    isEventHidden: state.isEventHidden,
    isHidden: state.isHidden,
    undoLastRemoval: state.undoLastRemoval,
    getExitReason: state.getExitReason,
    addToHiddenEvents: state.addToHiddenEvents,
    clearHiddenEvents: state.clearHiddenEvents
  }))
);

export const useFavorites = () => useAppStore(
  useShallow((state) => ({
    favoriteEvents: state.favoriteEvents,
    favoriteIds: state.favoriteIds,
    isFavorite: state.isFavorite,
    toggleFavorite: state.toggleFavorite,
    loadFavorites: state.loadFavorites
  }))
);

export const usePendingRemovals = () => useAppStore(
  useShallow((state) => ({
    pendingRemovals: state.pendingRemovals,
    addPendingRemoval: state.addPendingRemoval,
    undoPendingRemoval: state.undoPendingRemoval,
    clearPendingRemoval: state.clearPendingRemoval,
    getPendingRemoval: state.getPendingRemoval,
    updatePendingRemovalIndex: state.updatePendingRemovalIndex
  }))
);

// ===== VIEW MODE HOOK =====
export const useViewMode = () => useAppStore(
  useShallow((state) => ({
    viewMode: state.viewMode,
    setViewMode: state.setViewMode
  }))
);